import { Box, Button, Divider, Typography } from "@mui/material";
import { DomainColorSquares, RequirementBox } from "components/Requirements";
import { RequestFeedbackSteps, REQUIREMENT } from "helpers/constants";
import { RateSelector } from "pages/Feedback/RequestFeedback/Components";
import { FC, memo, useState, useMemo } from "react";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { CoachReview } from "./CoachReview";
import { CommentWidget } from "components/Comments";
import { Comment } from "@mui/icons-material";
import { useAuthUser } from "hooks/useAuthUser";
import { getRequirementDisplayData } from "utils/feedbackIter";
import { isFeedbackCoach } from "utils/feedback";

const skillHasCoachFeedback = (skill: {
  coach_rating?: number | null;
  coach_comments?: string | null;
}) =>
  skill.coach_rating != null || Boolean(skill.coach_comments?.trim());

const shouldShowCoachSection = (
  feedback: FeedbackType,
  skill: { coach_rating?: number | null; coach_comments?: string | null },
) =>
  feedback.feedback_type === "IN-PERSON" ||
  skillHasCoachFeedback(skill) ||
  feedback.status === FeedbackStatus.DONE;

const getCoachRating = (
  feedback: FeedbackType,
  skill: { coach_rating?: number | null },
  rate?: number,
) => rate ?? skill.coach_rating ?? feedback.coach_rate ?? null;

type FeedbackDetailProps = {
  feedback: FeedbackType;
  refetch?: () => void;
  rate?: number;
  onRateChange?: (value: number) => void;
  customLabels?: any[];
  allRequirements?: any[];
};

export const FeedbackDetailComponent: FC<FeedbackDetailProps> = ({
  feedback,
  refetch,
  rate,
  onRateChange,
  customLabels,
  allRequirements = [],
}) => {
  const { user, role } = useAuthUser();
  const isAssignedCoach = isFeedbackCoach(feedback, user?.user_id, role);
  const [displayCommentSection, setDisplayCommentSection] =
    useState<boolean>(false);
  const [openCoachReview, setOpenCoachReview] = useState(false);
  
  const iterDisplayData = useMemo(
    () => getRequirementDisplayData(feedback, allRequirements),
    [feedback, allRequirements],
  );
  const isITER = iterDisplayData.isITER;
  const skills = feedback.feedback_skills ?? [];
  const focusFeedbackLabel =
    feedback.tags
      ?.map((tag) => tag.tag?.name)
      .filter(Boolean)
      .join(", ") || "No focus feedback";

  const hasCoachFeedback = useMemo(
    () => (feedback.feedback_skills ?? []).some(skillHasCoachFeedback),
    [feedback.feedback_skills],
  );

  // For ITER feedbacks, group all fields per competency
  if (isITER) {
    return (
      <Box>
        {/* ITER Header */}
        <Box mt={2}>
          <RequirementBox
            requirement={{
              requirement_id: skills[0]?.requirement?.requirement_id || "",
              name: iterDisplayData.name,
              type: REQUIREMENT.REQUIREMENT_ITER,
            } as any}
            customLabels={customLabels}
          />
        </Box>
        <Divider sx={{ my: 2 }} />

        {/* Show Focus Feedback once for ITER */}
        {(feedback.feedback_type === "IN-PERSON" || hasCoachFeedback) && (
          <Box my={2}>
            <Typography variant="body1" fontWeight="bold">
              1. {RequestFeedbackSteps.questionFocusFeedback}
            </Typography>
            <Typography
              variant="body1"
              fontWeight="normal"
              color="text.secondary"
            >
              {focusFeedbackLabel}
            </Typography>
          </Box>
        )}

        {/* Each competency with all its fields */}
        {skills.map((skill, index) => (
          <Box
            key={skill.requirement?.requirement_id || skill.feedback_skill_id}
            sx={{ borderTop: index > 0 ? "1px solid rgba(0,0,0,0.2)" : "none", pt: index > 0 ? 3 : 0, mt: index > 0 ? 3 : 0 }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {skill.requirement?.name}
              </Typography>
              <Box mt={0.75}>
                <DomainColorSquares requirement={skill.requirement} size={12} />
              </Box>
            </Box>

            {/* Personal Reflection Section */}
            <Box my={2}>
              <Box mt={2}>
                <Typography variant="h6">Personal Reflection</Typography>
                <Divider sx={{ my: 1 }} />
              </Box>

              <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
                1. {RequestFeedbackSteps.question1Reflect}
              </Typography>
              <Typography variant="body1">{skill.student_comments || ""}</Typography>

              <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
                2. {RequestFeedbackSteps.question2Reflect}
              </Typography>
              <RateSelector
                value={skill.self_rating}
                requirement={skill.requirement}
                customLabels={customLabels}
              />
            </Box>

            {/* Coach Feedback Section for this competency */}
            {shouldShowCoachSection(feedback, skill) && (
              <Box my={2}>
                <Box mt={4}>
                  <Typography variant="h6">Coach Feedback</Typography>
                  <Divider sx={{ my: 1 }} />
                </Box>

                <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
                  1. {RequestFeedbackSteps.question1ReflectCoach}
                </Typography>
                <Typography variant="body1">{skill.coach_comments || ""}</Typography>

                <Box my={2}>
                  <Typography variant="body1" fontWeight="bold">
                    {RequestFeedbackSteps.question2ReflectCoach}
                  </Typography>
                  <RateSelector
                    value={getCoachRating(feedback, skill, rate)}
                    requirement={skill.requirement}
                    onChange={(value) => {
                      onRateChange?.(value);
                    }}
                    customLabels={customLabels}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ))}

        {/* Coach Review Modal and Add Button (only show once) */}
        {feedback.feedback_type !== "IN-PERSON" && (
          <>
            <CoachReview
              feedback={feedback}
              open={openCoachReview}
              onClose={() => setOpenCoachReview(false)}
              onAddFeedback={() => {
                refetch?.();
              }}
              customLabels={customLabels}
            />
          </>
        )}

        {isAssignedCoach &&
          feedback?.feedback_type !== "IN-PERSON" &&
          !hasCoachFeedback &&
          feedback?.status === FeedbackStatus.PENDING && (
            <Box mt={4} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenCoachReview(true)}
                sx={{ width: { xs: "100%", md: "inherit" } }}
              >
                Add Coach Feedback
              </Button>
            </Box>
          )}

        {/* Amendments section (show once for ITER) */}
        {(feedback.feedback_type === "IN-PERSON" || hasCoachFeedback) && (
          <Box
            sx={{ my: 2, display: "flex", justifyContent: "space-between" }}
          >
            <Box />
            <Button
              variant={displayCommentSection ? "outlined" : "contained"}
              color="primary"
              sx={{ width: { xs: "100%", md: "inherit" } }}
              onClick={() => {
                setDisplayCommentSection(!displayCommentSection);
              }}
              startIcon={<Comment />}
            >
              {displayCommentSection
                ? "Hide Amendments"
                : "Show Amendments"}
            </Button>
          </Box>
        )}

        {displayCommentSection && (
          <>
            <Divider sx={{ my: 2 }} />
            <CommentWidget
              title="Amendments"
              buttonLabel="Add Amendment"
            />
          </>
        )}
      </Box>
    );
  }

  // For non-ITER feedbacks, keep the original structure
  return (
    <Box>
      {skills.map((skill, index) => (
        <Box
          key={skill.requirement?.requirement_id || skill.feedback_skill_id || index}
          sx={{ borderTop: index === 1 ? "1px solid rgba(0,0,0,0.2)" : "none" }}
        >
          <Box mt={2}>
            {skill.requirement && (
              <RequirementBox
                requirement={skill.requirement}
                customLabels={customLabels}
              />
            )}
          </Box>
          <Divider sx={{ my: 2 }} />

          <Box my={2}>
            <Box mt={4}>
              <Typography variant="h6">Personal Reflection</Typography>
              <Divider sx={{ my: 1 }} />
            </Box>

            <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
              1. {RequestFeedbackSteps.question1Reflect}
            </Typography>
            <Typography variant="body1">{skill.student_comments}</Typography>

            <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
              2. {RequestFeedbackSteps.question2Reflect}
            </Typography>
            <RateSelector
              value={skill.self_rating}
              requirement={skill.requirement}
              customLabels={customLabels}
            />
          </Box>

          {feedback.feedback_type !== "IN-PERSON" && (
            <>
              <CoachReview
                feedback={feedback}
                open={openCoachReview}
                onClose={() => setOpenCoachReview(false)}
                onAddFeedback={() => {
                  refetch?.();
                }}
                customLabels={customLabels}
              />
            </>
          )}

          {isAssignedCoach &&
            feedback?.feedback_type !== "IN-PERSON" &&
            !hasCoachFeedback &&
            feedback?.status === FeedbackStatus.PENDING && (
              <Box mt={4} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenCoachReview(true)}
                  sx={{ width: { xs: "100%", md: "inherit" } }}
                >
                  Add Coach Feedback
                </Button>
              </Box>
            )}

          {shouldShowCoachSection(feedback, skill) && (
            <>
              <Box mt={4}>
                <Typography variant="h6">Coach Feedback</Typography>
                <Divider sx={{ my: 1 }} />
              </Box>
              <Box my={2}>
                <Typography variant="body1" fontWeight="bold">
                  1. {RequestFeedbackSteps.questionFocusFeedback}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="normal"
                  color="text.secondary"
                >
                  {focusFeedbackLabel}
                </Typography>
              </Box>

              <Typography variant="body1" fontWeight={600} sx={{ my: 1 }}>
                2. {RequestFeedbackSteps.question1ReflectCoach}
              </Typography>
              <Typography variant="body1">{skill?.coach_comments}</Typography>

              <Box
                sx={{ my: 2, display: "flex", justifyContent: "space-between" }}
              >
                <Box />
                <Button
                  variant={displayCommentSection ? "outlined" : "contained"}
                  color="primary"
                  sx={{ width: { xs: "100%", md: "inherit" } }}
                  onClick={() => {
                    setDisplayCommentSection(!displayCommentSection);
                  }}
                  startIcon={<Comment />}
                >
                  {displayCommentSection
                    ? "Hide Amendments"
                    : "Show Amendments"}
                </Button>
              </Box>

              {displayCommentSection && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <CommentWidget
                    title="Amendments"
                    buttonLabel="Add Amendment"
                  />
                </>
              )}

              <Box my={2}>
                <Typography variant="body1" fontWeight="bold">
                  {RequestFeedbackSteps.question2ReflectCoach}
                </Typography>
                <RateSelector
                  value={getCoachRating(feedback, skill, rate)}
                  requirement={skill.requirement}
                  onChange={(value) => {
                    onRateChange?.(value);
                  }}
                  customLabels={customLabels}
                />
              </Box>
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};

export const FeedbackDetail = memo(FeedbackDetailComponent);
